import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Core/Modal/Modal";
import AppText from "../Core/AppText/AppText";
import Button from "../Core/Button/Button";
import AppSelect from "../Core/AppSelect/AppSelect";
import LoadingIndicator from "../Shared/LoadingIndicator/LoadingIndicator";
import {
	useConsultantManagersByOrg,
	useTransferConsultants,
} from "../../networking/internal-user.hooks";
import { toast } from "../../utils/toast";

interface TransferConsultantsModalProps {
	sourceManagerId: string;
	sourceManagerName: string;
	organisationId: string;
	consultantCount: number;
	onClose: () => void;
	onSuccess: () => void;
}

const TransferConsultantsModal: React.FC<TransferConsultantsModalProps> = ({
	sourceManagerId,
	sourceManagerName,
	organisationId,
	consultantCount,
	onClose,
	onSuccess,
}) => {
	const { t } = useTranslation();
	const [targetManagerId, setTargetManagerId] = useState<string>("");

	const { managers: allManagers, isLoading } = useConsultantManagersByOrg(organisationId);
	const { mutateAsync: transferConsultants, isLoading: isTransferring } = useTransferConsultants();

	// Filter out the source manager and inactive/deleted users
	const managers = useMemo(
		() =>
			allManagers.filter(
				(m: any) => m.id !== sourceManagerId && m.active === true && m.isDeleted !== true
			),
		[allManagers, sourceManagerId]
	);

	const handleTransfer = async () => {
		if (!targetManagerId) {
			toast.error("Please select a target manager");
			return;
		}

		try {
			const data = await transferConsultants({ sourceManagerId, targetManagerId });

			if (data.failedCount === 0) {
				toast.success(t("TRANSFER_CONSULTANTS_SUCCESS", { count: data.successCount }));
			} else {
				toast.warning(
					t("TRANSFER_CONSULTANTS_PARTIAL", {
						success: data.successCount,
						total: data.totalConsultants,
						failed: data.failedCount,
					})
				);
			}
			onSuccess();
			onClose();
		} catch (error) {
			toast.error("An error occurred during transfer");
		}
	};

	const managerOptions = managers.map((manager) => ({
		id: manager.id,
		name: `${manager.firstName} ${manager.lastName}`,
	}));

	if (isLoading) {
		return (
			<Modal maxWidth="32rem" onClose={onClose}>
				<div className="flex flex-col items-center justify-center p-12">
					<LoadingIndicator showText={false} />
				</div>
			</Modal>
		);
	}

	return (
		<Modal maxWidth="32rem" onClose={onClose}>
			<div className="flex flex-col w-full">
				<AppText size="xm" weight="bold" margin="0 0 1.5rem 0">
					{t("TRANSFER_CONSULTANTS_TITLE")}
				</AppText>

				<AppText margin="0 0 1rem 0">
					{t("TRANSFER_CONSULTANTS_COUNT", { count: consultantCount })}
				</AppText>

				<AppText margin="0 0 1.5rem 0" color="gray">
					{t("FROM")}: {sourceManagerName}
				</AppText>

				{managers.length === 0 ? (
					<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
						<AppText color="danger" size="s">
							{t("TRANSFER_CONSULTANTS_NO_MANAGERS")}
						</AppText>
					</div>
				) : (
					<div className="mb-6">
						<AppSelect
							label="TRANSFER_CONSULTANTS_SELECT_MANAGER"
							options={managerOptions}
							value={targetManagerId}
							onChange={(value) => setTargetManagerId(value)}
							placeholder="TRANSFER_CONSULTANTS_SELECT_MANAGER"
							mandatory
							margin="0"
						/>
					</div>
				)}

				<div className="flex justify-between gap-4 mt-4">
					<Button
						width="48%"
						backgroundColor="primary"
						textColor="white"
						onClick={handleTransfer}
						disabled={!targetManagerId || isTransferring || managers.length === 0}
						loading={isTransferring}
					>
						{t("TRANSFER_CONSULTANTS_CONFIRM")}
					</Button>
					<Button
						width="48%"
						backgroundColor="dividerGray"
						textColor="black"
						onClick={onClose}
						disabled={isTransferring}
					>
						{t("TRANSFER_CONSULTANTS_CANCEL")}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default TransferConsultantsModal;
